import { Fragment, ReactNode, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    LEVEL_LABEL,
    MATCH_LABEL,
    SOURCE_LABEL,
    STATION_KIND,
    STATION_STATE,
    TraceData,
    TraceDataRef,
    TraceEntry,
    TraceIo,
    TraceJourney,
    TraceSource,
    TraceStation,
    TraceStationKind,
    TraceStationState,
} from '../../api/trace';
import { TraceCallout } from './TraceCallout';
import { TraceChip, TraceTone } from './TraceChip';
import { TraceDataRefs } from './TraceDataRefs';
import { TraceIoBlock, TraceValue } from './TraceIo';
import { TraceRows } from './TraceRows';
import { InterpretationNote } from '../Interpretation/InterpretationNote';

export interface ITraceFlowProps {
    journey: TraceJourney;
    /** Every row that was read, newest first, station or no station. */
    timeline: TraceEntry[];
    /** Fetches one stored body, when somebody opens it. */
    read: (reference: TraceDataRef) => Promise<TraceData>;
    onShowStackTraces: () => void;
    stackTracesLoaded: boolean;
    /**
     * Draws the button of one action, at the step that action acts on.
     *
     * A callback rather than the buttons themselves: the panel owns them, knows which one is
     * recommended and holds the handlers, and the chain only knows where they belong. Which is
     * the thing a reader looking at a stuck step wants - the way onwards next to what is stuck,
     * not only in a row above it.
     */
    renderAction: (kind: string) => ReactNode;
}

/** The icon of each kind of station, so a step is recognisable before it is read. */
const KIND_ICON: Record<TraceStationKind, IconProp> = {
    System: 'cloud',
    Http: 'right-to-bracket',
    Queue: 'layer-group',
    Worker: 'gear',
    Store: 'database',
    Poison: 'skull',
};

/** The order the legend lists them in: the order they occur on the road. */
const KIND_ORDER: TraceStationKind[] = [
    'System',
    'Http',
    'Queue',
    'Worker',
    'Store',
    'Poison',
];

/**
 * The icon of each state, next to the word for it.
 *
 * Both, and not one of the two. A word alone is something you have to read in full before you
 * know whether to care; an icon alone is a colour with a shape, and this panel has six states
 * rather than three precisely because the honest ones - "geen spoor", "niet aan de orde" - do
 * not map onto a traffic light.
 */
const STATE_ICON: Record<TraceStationState, IconProp> = {
    Passed: 'circle-check',
    Here: 'hourglass-half',
    Failed: 'circle-exclamation',
    Blocked: 'ban',
    Unknown: 'circle-question',
    Skipped: 'minus',
};

/** How loud a row from each table is. See SOURCE_LABEL for why these are kept apart. */
const SOURCE_TONE: Record<TraceSource, TraceTone> = {
    QueueMessageLog: 'error',
    SyncLog: 'info',
    Log: 'muted',
    Queue: 'warn',
};

const SOURCE_ICON: Record<TraceSource, IconProp> = {
    QueueMessageLog: 'triangle-exclamation',
    SyncLog: 'database',
    Log: 'list-ul',
    Queue: 'layer-group',
};

/** The key of the group that holds every row no station claimed. */
const LOOSE = 'log:loose';

const LOOSE_HINT =
    'Regels uit het algemene logboek. Die worden vanuit de hele app geschreven en zeggen niet bij welke stap ze horen, dus ze hangen hier in plaats van onder een stap die ze misschien niet geschreven heeft.';

/**
 * The anchor of one step, so the verdict can jump to the step it is about.
 *
 * Station keys carry colons - `queue:chargebee` - which are fine in an id and awkward in
 * everything that reads one, so they are flattened here. Shared with the panel: two ways of
 * spelling the same anchor is a link that scrolls nowhere.
 */
export const stationAnchorId = (key: string): string =>
    `trace-step-${key.replace(/[^a-zA-Z0-9]+/g, '-')}`;

/** A moment, to the second: two attempts in one minute are a different story from one. */
const moment = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const parsed = Date.parse(iso);

    return Number.isNaN(parsed)
        ? ''
        : new Date(parsed).toLocaleString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
          });
};

/** A day partition, for the origin line inside a row. */
const dayLabel = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const parsed = Date.parse(iso);

    return Number.isNaN(parsed)
        ? ''
        : new Date(parsed).toLocaleDateString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          });
};

/**
 * One row of a step's log.
 *
 * A failed attempt and a log line look different on purpose. Two routes through the send
 * handler end an object's journey without ever writing an attempt row, so a screen that
 * paints every row red teaches you to read past the one line that says what really
 * happened.
 */
const TimelineRow = (props: {
    entry: TraceEntry;
    read: (reference: TraceDataRef) => Promise<TraceData>;
    onShowStackTraces: () => void;
    stackTracesLoaded: boolean;
}) => {
    const { entry } = props;
    const [open, setOpen] = useState<boolean>(false);

    const source = SOURCE_LABEL[entry.source];
    const level = LEVEL_LABEL[entry.level];
    const message = entry.functionalMessage || entry.message || '';

    // An older API would send no pointers at all rather than an empty list, and a panel
    // that falls over on a missing field is worse than one that offers no button.
    const data = entry.data ?? [];
    const hasDetail =
        !!entry.interpretation ||
        !!entry.message ||
        !!entry.queueMessage ||
        entry.hasStackTrace ||
        data.length > 0;

    /*
        The level only where it says something the source does not.

        A row in the attempt log is a failure by definition, so "fout" next to "poging" is one
        badge saying the same thing twice - and a chain of duplicated badges is exactly what
        made the old row unreadable. A warning in the general log, on the other hand, is the
        distinction that matters: it is the one row of the lot that somebody wrote on purpose.
    */
    const showLevel =
        entry.source !== 'QueueMessageLog' &&
        (entry.level === 'Warning' || entry.level === 'Error');

    return (
        <li
            className={`trace-timeline__row trace-timeline__row--${entry.source.toLowerCase()} trace-timeline__row--${entry.level.toLowerCase()}`}
        >
            <span className="trace-timeline__dot" aria-hidden="true" />

            <button
                type="button"
                className="trace-timeline__head"
                onClick={() => setOpen((current) => !current)}
                aria-expanded={open}
                disabled={!hasDetail}
            >
                <span className="trace-timeline__time">{moment(entry.at)}</span>

                <TraceChip
                    tone={SOURCE_TONE[entry.source]}
                    icon={SOURCE_ICON[entry.source]}
                    hint={source.hint}
                >
                    {source.label}
                </TraceChip>

                {entry.attempt != null && (
                    <TraceChip
                        label="poging"
                        tone="neutral"
                        hint="De hoeveelste bewaarde poging van die dag. Er kunnen er ontbreken - zie de kanttekeningen onderaan."
                    >
                        {entry.attempt}
                    </TraceChip>
                )}

                {entry.state && (
                    <TraceChip
                        label="stand"
                        tone="neutral"
                        hint="Wat de synclog bij deze regel als stand noteerde."
                    >
                        {entry.state}
                    </TraceChip>
                )}

                {showLevel && (
                    <TraceChip
                        tone={level.tone}
                        hint="Het niveau waarop deze regel is weggeschreven."
                    >
                        {level.label}
                    </TraceChip>
                )}

                <span className="trace-timeline__message" title={message}>
                    {entry.interpretation ? (
                        <InterpretationNote
                            interpretation={entry.interpretation}
                            compact
                        />
                    ) : (
                        message || <em>geen melding</em>
                    )}
                </span>

                {data.length > 0 && (
                    <span
                        className="trace-timeline__has-data"
                        title="Bij deze regel is de opgeslagen data op te vragen."
                    >
                        <FontAwesomeIcon icon="file-code" />
                    </span>
                )}

                {hasDetail && (
                    <FontAwesomeIcon
                        icon={open ? 'angle-up' : 'angle-down'}
                        className="trace-timeline__chevron"
                    />
                )}
            </button>

            {open && (
                <div className="trace-timeline__detail">
                    {entry.interpretation && (
                        <InterpretationNote
                            interpretation={entry.interpretation}
                        />
                    )}

                    {/*
                        The message this row was written about, as the message it is rather than
                        as a line of prose. It is the one field on a failed attempt that says
                        what the handler was actually handed, and it is worth copying somewhere
                        else - which is why it gets the same treatment as the messages on the
                        input and output rows of a step.
                    */}
                    {entry.queueMessage && (
                        <div className="trace-timeline__message-block">
                            <span className="trace-timeline__message-label">
                                <FontAwesomeIcon
                                    icon="envelope"
                                    className="me-2"
                                />
                                Queuebericht dat deze regel opleverde
                            </span>

                            <TraceValue
                                value={entry.queueMessage}
                                label="Queuebericht"
                                hint="Precies het bericht dat de function toen verwerkte. Dit is gelezen, niet samengesteld."
                            />
                        </div>
                    )}

                    <dl className="trace-detail">
                        {entry.errorType && (
                            <>
                                <dt>Type</dt>
                                <dd className="trace-detail__mono">
                                    {entry.errorType}
                                </dd>
                            </>
                        )}

                        {entry.message && (
                            <>
                                <dt>Ruwe melding</dt>
                                <dd>{entry.message}</dd>
                            </>
                        )}

                        <dt>Herkomst</dt>
                        <dd>
                            {MATCH_LABEL[entry.matchedOn]}
                            {entry.day &&
                                `, dagpartitie ${dayLabel(entry.day)}`}
                        </dd>
                    </dl>

                    {/*
                        The object this row was about, one click further. This is the whole
                        point of the row for anybody chasing a failure: the message says what
                        broke, and only the body says what it broke on.
                    */}
                    <TraceDataRefs
                        owner={entry.id}
                        refs={data}
                        read={props.read}
                    />

                    {entry.hasStackTrace &&
                        (entry.stackTrace ? (
                            <pre className="trace-stack">
                                {entry.stackTrace}
                            </pre>
                        ) : (
                            <button
                                type="button"
                                className="requeue-link"
                                onClick={props.onShowStackTraces}
                                disabled={props.stackTracesLoaded}
                                title="Haalt de trace opnieuw op, nu met de stacktraces erbij. Die zijn standaard weggelaten omdat ze het antwoord vele malen groter maken."
                            >
                                Stacktrace ophalen
                            </button>
                        ))}
                </div>
            )}
        </li>
    );
};

/** One step of the road, with everything that is known and written at it. */
const Step = (props: {
    station: TraceStation;
    entries: TraceEntry[];
    current: boolean;
    failed: boolean;
    openByDefault: boolean;
    read: (reference: TraceDataRef) => Promise<TraceData>;
    onShowStackTraces: () => void;
    stackTracesLoaded: boolean;
    renderAction: (kind: string) => ReactNode;
}) => {
    const { station } = props;
    const state = STATION_STATE[station.state];
    const kind = STATION_KIND[station.kind];

    const [open, setOpen] = useState<boolean>(props.openByDefault);
    const [expanded, setExpanded] = useState<boolean>(false);

    // An older API sends none of these rather than an empty list.
    const data = station.data ?? [];
    const rows = station.rows ?? [];
    const actionKinds = station.actionKinds ?? [];

    /*
        A step that says only that the road passes through here gives up its height.

        Half the chain cannot be observed per object - accepting a webhook writes no row - and
        those steps used to take up as much of the page as the step that actually broke, faded
        by a quarter. Which is how a reader scrolls past the one card that had something to
        say. So they keep their place, their icon and their honest summary, and fold the rest
        away until somebody asks. Never when there is a warning, an action or a row to read:
        those are the things somebody came for.
    */
    const quiet =
        (station.state === 'Unknown' || station.state === 'Skipped') &&
        props.entries.length === 0 &&
        data.length === 0 &&
        rows.length === 0 &&
        actionKinds.length === 0 &&
        !station.warning &&
        !props.current &&
        !props.failed;

    const folded = quiet && !expanded;

    return (
        <li
            id={stationAnchorId(station.key)}
            className={[
                'trace-step',
                `trace-step--${station.state.toLowerCase()}`,
                `trace-step--kind-${station.kind.toLowerCase()}`,
                props.current ? 'trace-step--current' : '',
                props.failed ? 'trace-step--failed-here' : '',
                folded ? 'trace-step--folded' : '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <span
                className="trace-step__rail"
                title={kind.hint}
                aria-hidden="true"
            >
                <FontAwesomeIcon icon={KIND_ICON[station.kind]} />
            </span>

            <div className="trace-step__card">
                <div className="trace-step__body">
                    <div className="trace-step__head">
                        <span className="trace-step__title">
                            {station.title}
                        </span>

                        {/*
                            What sort of point this is, in a word. The icon on the rail says the
                            same thing to somebody who has learned the six shapes; the word says
                            it to everybody else, and whether pressing anything makes sense
                            depends entirely on it - a queue can be refilled, a table cannot.
                        */}
                        <span className="trace-step__kind" title={kind.hint}>
                            {kind.label}
                        </span>

                        {station.subtitle && (
                            <span className="trace-step__subtitle">
                                {station.subtitle}
                            </span>
                        )}

                        {/*
                            One badge, not two. "Staat hier nu" and "hier staat het" were the same
                            claim in two words next to each other, because the state and the
                            current-step marker are the same fact on the one step that is both.

                            And on a step that simply went well, no badge at all: a tick, and the
                            green line running down from it. "Gepasseerd" was the most repeated
                            word on the panel and the least informative one - it appeared on every
                            step that behaved, which is most of them, so it turned into a texture
                            the eye skips while it looks for the one step that did not. The colour
                            of the rail says the same thing without occupying a line of the card.
                        */}
                        {station.state === 'Passed' ? (
                            <span
                                className="trace-step__passed"
                                title={
                                    props.current
                                        ? `${state.hint} Zover is dit object gekomen.`
                                        : state.hint
                                }
                                aria-label={state.label}
                            >
                                <FontAwesomeIcon
                                    icon={STATE_ICON[station.state]}
                                />
                            </span>
                        ) : (
                            <TraceChip
                                tone={state.tone}
                                icon={STATE_ICON[station.state]}
                                hint={
                                    props.current
                                        ? `${state.hint} Zover is dit object gekomen.`
                                        : state.hint
                                }
                            >
                                {state.label}
                            </TraceChip>
                        )}

                        {props.current && station.state !== 'Here' && (
                            <TraceChip
                                tone="info"
                                label="nu"
                                hint="Verder dan deze stap is dit object niet aantoonbaar gekomen."
                            >
                                tot hier gekomen
                            </TraceChip>
                        )}

                        {station.at && (
                            <span
                                className="trace-step__at"
                                title={station.atHint ?? undefined}
                            >
                                {moment(station.at)}
                            </span>
                        )}
                    </div>

                    {/*
                        The summary and the facts stand open rather than behind a click. They are
                        two lines and a handful of values, and they are what the step is for -
                        hiding them was how a step could be marked "gepasseerd" and tell a reader
                        who clicked it nothing at all.
                    */}
                    {station.summary && (
                        <p
                            className={
                                folded
                                    ? 'trace-step__summary trace-step__summary--folded'
                                    : 'trace-step__summary'
                            }
                        >
                            {station.summary}
                        </p>
                    )}

                    {quiet && (
                        <button
                            type="button"
                            className="trace-step__fold"
                            onClick={() => setExpanded((current) => !current)}
                            aria-expanded={expanded}
                        >
                            <FontAwesomeIcon
                                icon={expanded ? 'angle-up' : 'angle-down'}
                                className="me-2"
                            />
                            {expanded
                                ? 'Minder over deze stap'
                                : 'Wat hoort hier te gebeuren?'}
                        </button>
                    )}

                    {!folded && station.facts.length > 0 && (
                        <dl className="trace-step__facts">
                            {station.facts.map((fact) => (
                                <div
                                    className="trace-step__fact"
                                    key={`${fact.label}-${fact.value}`}
                                >
                                    <dt title={fact.hint ?? undefined}>
                                        {fact.label}
                                    </dt>
                                    <dd
                                        className={
                                            fact.mono
                                                ? 'trace-detail__mono'
                                                : undefined
                                        }
                                    >
                                        {fact.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    )}

                    {!folded && station.note && (
                        <TraceCallout tone="muted">{station.note}</TraceCallout>
                    )}

                    {/*
                        About the environment rather than about the object, and therefore separate
                        from the note. It used to replace it, which cost the step its own diagnosis
                        on exactly the step that had one.
                    */}
                    {station.warning && (
                        <TraceCallout tone="warn">
                            {station.warning}
                        </TraceCallout>
                    )}

                    {/*
                        The rows this step writes, as rows. The summary above says whether the
                        copy is still there; this says in which table, under which two keys,
                        and with what in the columns - which is what somebody needs to go and
                        look at it, and what no sentence can hand over.
                    */}
                    {!folded && rows.length > 0 && (
                        <TraceRows
                            owner={station.key}
                            rows={rows}
                            read={props.read}
                        />
                    )}

                    {!folded && (
                        <TraceDataRefs
                            owner={station.key}
                            refs={data}
                            read={props.read}
                        />
                    )}

                    {/* The way onwards, at the step it acts on. */}
                    {actionKinds.length > 0 && (
                        <div className="trace-step__actions">
                            {actionKinds.map((kindOfAction) => (
                                <div key={kindOfAction}>
                                    {props.renderAction(kindOfAction)}
                                </div>
                            ))}
                        </div>
                    )}

                    {props.entries.length > 0 && (
                        <div className="trace-step__log">
                            <button
                                type="button"
                                className="trace-step__log-toggle"
                                onClick={() => setOpen((current) => !current)}
                                aria-expanded={open}
                            >
                                <FontAwesomeIcon
                                    icon={open ? 'angle-up' : 'angle-down'}
                                    className="me-2"
                                />
                                {props.entries.length} regel(s) op deze stap
                            </button>

                            {open && (
                                <ul className="trace-timeline">
                                    {props.entries.map((entry) => (
                                        <TimelineRow
                                            key={entry.id}
                                            entry={entry}
                                            read={props.read}
                                            onShowStackTraces={
                                                props.onShowStackTraces
                                            }
                                            stackTracesLoaded={
                                                props.stackTracesLoaded
                                            }
                                        />
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
};

/**
 * Two things are the same thing travelling, for the purpose of not printing it twice.
 *
 * What one step hands on and what the next one is handed is one message, described from two
 * ends. Both descriptions used to be drawn - once under the step above, once above the step
 * below - which put the same string on the screen twice with a card boundary between the
 * copies, and left a reader checking whether they were really identical.
 */
const same = (left: TraceIo, right: TraceIo): boolean =>
    left.kind === right.kind &&
    (left.value ?? null) === (right.value ?? null) &&
    left.label === right.label;

/**
 * What travels from one step to the next, drawn on the line between them.
 *
 * The thing the panel was missing, and the reason it was missing it is the shape it used to
 * have: the message a step hands on sat inside that step's card, and the message the next
 * step reads sat inside the next one, so the one string that connects the two was printed
 * twice, in two boxes, with a card border between the copies. Every worker in this connector
 * is handed exactly one short comma-separated line and looks the rest up itself, so that
 * string is what half the questions on this screen are about.
 *
 * Here it is one block on the rail itself, between the two steps it belongs to, in the gap
 * where the line runs. Which is also where the eye goes when it follows a chain downwards.
 */
const Handover = (props: {
    /** What the step above hands on. */
    out: TraceIo[];
    /** What the step below is handed, minus whatever the line above already said. */
    inn: TraceIo[];
    /** A key that is unique per gap, so two blocks cannot share an open state. */
    owner: string;
    /** Whether the step above this gap went through in good order. */
    passed: boolean;
    read: (reference: TraceDataRef) => Promise<TraceData>;
}) => (
    <li
        className={`trace-handover${
            props.passed ? ' trace-handover--passed' : ''
        }`}
    >
        <div className="trace-handover__body">
            {props.out.length > 0 && (
                <TraceIoBlock
                    direction="out"
                    items={props.out}
                    owner={`${props.owner}|out`}
                    read={props.read}
                />
            )}

            {props.inn.length > 0 && (
                <TraceIoBlock
                    direction="in"
                    items={props.inn}
                    owner={`${props.owner}|in`}
                    read={props.read}
                />
            )}
        </div>
    </li>
);

/**
 * What the shapes on the rail mean, once, above the chain.
 *
 * Six icons down the left of a page is a code, and a code without a key is decoration. It
 * lists only the kinds this particular road has, because a legend that explains something
 * the reader cannot see is one more thing to check against the screen.
 */
const Legend = (props: { kinds: TraceStationKind[] }) => (
    <ul className="trace-legend">
        {KIND_ORDER.filter((kind) => props.kinds.includes(kind)).map((kind) => (
            <li
                key={kind}
                className={`trace-legend__item trace-legend__item--${kind.toLowerCase()}`}
                title={STATION_KIND[kind].hint}
            >
                <span className="trace-legend__node">
                    <FontAwesomeIcon icon={KIND_ICON[kind]} />
                </span>
                {STATION_KIND[kind].label}
            </li>
        ))}
    </ul>
);

/**
 * The road one object travelled, top to bottom, with the log hanging off the steps.
 *
 * One list rather than a chain of cards with a filtered table under it. That split was the
 * problem: the cards were the same size whether they had anything to say or not, their facts
 * sat behind a click, and clicking one filtered the table below to the rows written at that
 * step - which for eight of the ten steps is none, so the answer to "what happened here" was
 * an empty card above an empty table.
 *
 * So the steps run down the page in the order the object passes them, each carrying its own
 * time, its own state, what is known at it, what it was handed and what it handed on, and the
 * rows that were written there. The rows that name no step - the general log writes them from
 * anywhere in an app - are grouped at the end rather than hidden, because that is where they
 * belong and nowhere is not an option.
 */
export const TraceFlow = (props: ITraceFlowProps) => {
    const { journey, timeline } = props;

    /** Oldest first inside a step: attempt one above attempt two reads as progress. */
    const perStation = useMemo(() => {
        const grouped = new Map<string, TraceEntry[]>();

        // On the parsed moment rather than on the string. A row with no timestamp falls back
        // to its day partition, and comparing "2026-08-14T09:12:03+02:00" with
        // "2026-08-14T00:00:00" as text puts the two in whichever order the offset suffix
        // happens to sort in.
        const when = (entry: TraceEntry): number =>
            Date.parse(entry.at ?? entry.day) || 0;

        [...timeline]
            .sort((left, right) => when(left) - when(right))
            .forEach((entry) => {
                const key = entry.stationKey ?? LOOSE;
                const list = grouped.get(key);

                if (list) {
                    list.push(entry);
                } else {
                    grouped.set(key, [entry]);
                }
            });

        return grouped;
    }, [timeline]);

    const loose = perStation.get(LOOSE) ?? [];

    return (
        <div className="trace-flow">
            <div className="trace-flow__head">
                <span className="trace-flow__route">
                    {journey.source}
                    <FontAwesomeIcon
                        icon="arrow-right"
                        className="trace-flow__route-arrow"
                    />
                    {journey.target}
                </span>

                <Legend
                    kinds={journey.stations.map((station) => station.kind)}
                />
            </div>

            <ol className="trace-flow__steps">
                {journey.stations.map((station, index) => {
                    const entries = perStation.get(station.key) ?? [];
                    const next = journey.stations[index + 1];

                    /*
                        The gap under this step: what it hands on, plus whatever the step
                        below is handed that this one did not already say. The second half is
                        rarely anything - the two are usually one message - and where it is
                        something, it is the case worth seeing: a step that reads more than
                        the step above it wrote.
                    */
                    const out = station.output ?? [];
                    const inn = (next?.input ?? []).filter(
                        (item) => !out.some((sent) => same(sent, item))
                    );

                    return (
                        <Fragment key={station.key}>
                            {/*
                                The very first thing that arrives, above the first step. It
                                comes from outside the chain, so there is no step above to
                                hand it on.
                            */}
                            {index === 0 && (station.input ?? []).length > 0 && (
                                <Handover
                                    out={[]}
                                    inn={station.input ?? []}
                                    owner={`before|${station.key}`}
                                    passed={false}
                                    read={props.read}
                                />
                            )}

                            <Step
                                station={station}
                                entries={entries}
                                current={journey.currentKey === station.key}
                                failed={journey.failedKey === station.key}
                                // Open where something went wrong or where the object stands
                                // now. Everywhere else the count is enough until somebody asks.
                                openByDefault={
                                    entries.length > 0 &&
                                    (journey.failedKey === station.key ||
                                        journey.currentKey === station.key)
                                }
                                read={props.read}
                                onShowStackTraces={props.onShowStackTraces}
                                stackTracesLoaded={props.stackTracesLoaded}
                                renderAction={props.renderAction}
                            />

                            {(out.length > 0 || inn.length > 0) && (
                                <Handover
                                    out={out}
                                    inn={inn}
                                    owner={station.key}
                                    passed={station.state === 'Passed'}
                                    read={props.read}
                                />
                            )}
                        </Fragment>
                    );
                })}

                {/*
                    The rows no step claimed, at the end of the road rather than nowhere.
                    Two of the routes that end an object's journey write only here, so this
                    group is regularly the only thing on the page that says what happened.
                */}
                {loose.length > 0 && (
                    <Step
                        station={{
                            key: LOOSE,
                            kind: 'Store',
                            title: 'Losse logregels',
                            subtitle: 'algemeen logboek',
                            state: 'Unknown',
                            summary: LOOSE_HINT,
                            facts: [],
                            entries: loose.length,
                            actionKinds: [],
                            data: [],
                            input: [],
                            output: [],
                        }}
                        entries={loose}
                        current={false}
                        failed={false}
                        openByDefault
                        read={props.read}
                        onShowStackTraces={props.onShowStackTraces}
                        stackTracesLoaded={props.stackTracesLoaded}
                        renderAction={props.renderAction}
                    />
                )}
            </ol>
        </div>
    );
};
